import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useCreateTemplateSection } from '@/services/query/entity-templates-queries';
import { useToast } from '@/components/ui/use-toast';

interface AddSectionButtonProps {
  templateId: number;
  entityId: number;
  onSuccess?: () => void; // Optional callback for when section is created successfully
}

export const AddSectionButton: React.FC<AddSectionButtonProps> = ({ templateId, entityId, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const createSectionMutation = useCreateTemplateSection();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sectionName.trim()) return;

    // Check if templateId and entityId are valid
    if (!templateId || !entityId) {
      toast({
        title: "Error",
        description: "Template ID or Entity ID is missing",
        variant: "destructive",
      });
      return;
    }

    try {
      await createSectionMutation.mutateAsync({
        templateId,
        name: sectionName,
        entityId
      });

      toast({
        title: "Success",
        description: `Section "${sectionName}" created successfully`,
      });

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset and close
      setSectionName('');
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating section:', error);
      toast({
        title: "Error",
        description: "Failed to create section",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="w-full mt-4 border-dashed"
        onClick={() => setIsOpen(true)}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add New Section
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Section</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="section-name">Section Name</Label>
                <Input
                  id="section-name"
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  placeholder="Enter section name"
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createSectionMutation.isPending || !sectionName.trim()}
              >
                {createSectionMutation.isPending ? 'Creating...' : 'Create Section'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
